/**
 * A one-signal event bus: "the current credentials no longer work; the user has
 * to authenticate again".
 *
 * It exists because the two things that know this are at opposite ends of the
 * app. The API layer finds out — a token cannot be acquired, or the backend
 * answers 401 — and the auth gate is the only thing that can do something about
 * it. Wiring them directly would mean either the gate polling, or `lib/api`
 * importing a component; a module both can import is neither.
 *
 * Deliberately not a React context: `fetchWithAuth` is called from plain
 * functions with no component tree around them (`lib/api/*.ts`), so a hook-only
 * channel could not reach it.
 *
 * With no subscriber this is a no-op, which is what the standalone browser build
 * wants — there, MSAL's own redirect handling already covers re-authentication.
 * Only the Teams tab subscribes today.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Subscribe to "authentication required". Returns an unsubscribe function, so a
 * caller in `useEffect` can hand it straight back as the cleanup.
 */
export function onAuthenticationRequired(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announce that re-authentication is needed.
 *
 * Iterates a copy, and swallows a throwing listener, so that one bad subscriber
 * can neither cut the notification short for the others nor propagate out into
 * whichever request happened to discover the problem.
 */
export function notifyAuthenticationRequired(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch (e) {
      console.error("[auth] authentication-required listener threw", e);
    }
  }
}
